import {lazy,Suspense,useEffect,useState} from 'react';
import {AnimatePresence,motion} from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import Reveal from './components/Reveal';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import BrandMark from './components/BrandMark';
import Hero from './components/Hero';
import OptimizedImage from './components/OptimizedImage';
import RoomPage from './pages/RoomPage';
import InquiryWidget from './components/InquiryWidget';
import {useSiteImages} from './hooks/useSiteImages';
import {useRooms,featureList} from './hooks/useRooms';
import {useGallery} from './hooks/useGallery';
import {GALLERY_CATEGORIES} from './data/imageSlots';
import { BOOKING_URL } from './data/booking';
import './App.css';
import './theme.css';

const AdminPage=lazy(()=> import('./admin/AdminPage'));

const photos=[
  {category: 'Stay',title: 'A quiet place to land',src: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=1000&q=85'},
  {category: 'Wildlife',title: 'Into the wild',src: 'https://images.unsplash.com/photo-1549366021-9f761d450615?auto=format&fit=crop&w=1000&q=85'},
  {category: 'Dining',title: 'Flavours of the island',src: 'https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1000&q=85'},
  {category: 'Nature',title: 'The golden hour',src: 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=1000&q=85'},
  {category: 'Stay',title: 'Slow mornings, made better',src: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1000&q=85'},
  {category: 'Pool',title: 'A little time to unwind',src: 'https://images.unsplash.com/photo-1572331165267-854da2b10ccc?auto=format&fit=crop&w=1000&q=85'}
];

const reviews=[
  {quote: 'A peaceful little sanctuary after a day on safari. The team made us feel at home from the moment we arrived.',name: 'Amelia R.',from: 'United Kingdom',initials: 'AR'},
  {quote: 'Beautiful surroundings, thoughtful hospitality, and the perfect base for exploring Yala. We wished we had stayed longer.',name: 'Daniel & Mei',from: 'Singapore',initials: 'DM'},
  {quote: 'The best kind of escape: warm, relaxed and wonderfully close to nature. We left already planning our return.',name: 'Sofia L.',from: 'Australia',initials: 'SL'}
];

const roomTypes=[
  {title: 'Deluxe Room',image: 'photo-1611892440504-42a792e24d32',description: 'A calm, considered retreat for couples and solo travellers.',features: ['King bed','Garden view','Private veranda']},
  {title: 'Family Suite',image: 'photo-1615571022214-7cb3d6a9c3f6',description: 'Room to slow down together, with comfort for the whole family.',features: ['Two bedrooms','Lounge space','Garden access']},
  {title: 'Safari Villa',image: 'photo-1613490493576-7fde63acd811',description: 'A little more privacy, wrapped in the quiet of the tropics.',features: ['Private terrace','Outdoor shower','King bed']}
];

const experiences=[
  {label: 'Out in the wild',title: 'Yala safari, at your own pace',text: 'Set out early with a local guide, watch the landscape wake up, and keep your eyes open for the park’s remarkable wildlife.',image: 'photo-1549366021-9f761d450615',icon: 'Compass'},
  {label: 'A taste of Sri Lanka',title: 'Gather around the table',text: 'Enjoy island-inspired cooking, fresh local ingredients and the pleasure of a long, unhurried meal.',image: 'photo-1547592180-85f173990554',icon: 'Coffee'},
  {label: 'Take it slow',title: 'Make room for stillness',text: 'Spend a quiet afternoon by the pool, follow a nature trail, or find a favourite spot to watch the sky change.',image: 'photo-1572331165267-854da2b10ccc',icon: 'Sun'}
];

function Eyebrow({children,light=false}) {
  return <span className={`eyebrow ${light ? 'eyebrow--light' : ''}`}>{children}</span>;
}

function About() {
  const {url}=useSiteImages();
  return (
    <section className="section about" id="about">
      <div className="about__copy">
        <Reveal>
          <Eyebrow>A PLACE TO EXHALE</Eyebrow>
          <h2>Close to the wild.<br /><em>Made for slowing down.</em></h2>
          <p className="lead">A welcoming hideaway for curious travellers, where the rhythm of nature sets the pace.</p>
          <p>Just a short journey from Yala National Park, Leopard Bliss brings together thoughtful comfort, warm Sri Lankan hospitality and the restorative feeling of being close to nature. Come for the wildlife. Stay for the stillness.</p>
          <a className="text-link" href="#experience">Find your kind of escape <SafeIcon name="ArrowRight" /></a>
        </Reveal>
        <div className="stats">
          {[['01','Thoughtful stay'],['02','Safari nearby'],['03','Island warmth']].map(([number,label])=> (
            <div className="stat" key={number}><strong>{number}</strong><span>{label}</span></div>
          ))}
        </div>
      </div>
      <Reveal className="about__visual">
        <OptimizedImage className="about__main-image" src={url('about_main')} alt="A tropical boutique villa surrounded by greenery" loading="lazy" sizes="(max-width: 720px) 86vw, 40vw" width={1000} height={900} />
        <div className="about__inset">
          <OptimizedImage src={url('about_inset')} alt="Warm sunrise over a quiet landscape" loading="lazy" sizes="(max-width: 720px) 40vw, 18vw" width={600} height={500} />
        </div>
        <div className="about__note"><SafeIcon name="MapPin" /><span>Yala country,<br />Sri Lanka</span></div>
      </Reveal>
    </section>
  );
}

function Rooms() {
  const {rooms}=useRooms();
  return (
    <section className="section rooms" id="rooms">
      <div className="section-heading">
        <Reveal><Eyebrow>REST, YOUR WAY</Eyebrow><h2>Find your <em>somewhere.</em></h2></Reveal>
        <p>Comfortable, characterful spaces to return to after a day outdoors.</p>
      </div>
      <div className="room-grid">
        {rooms.map((room,index)=> (
          <Reveal className="room-card" key={room.slug} delay={index * 0.1}>
            <a className="room-card__image" href={`/${room.name}`} aria-label={`View the ${room.name} room`}>
              <OptimizedImage src={room.cover_url} alt={`${room.name} room`} loading="lazy" sizes="(max-width: 720px) 86vw, (max-width: 1000px) 28vw, 25vw" width={900} height={650} />
              <span className="room-card__number">0{index + 1}</span>
            </a>
            <div className="room-card__body">
              <div className="room-card__title"><h3>{room.name}</h3><span>From $— <small>/ night</small></span></div>
              <p>{room.tagline}</p>
              <div className="amenities">{featureList(room).slice(0,3).map((feature)=> <span key={feature}><SafeIcon name="Check" />{feature}</span>)}</div>
              <a className="text-link" href={`/${room.name}`}>View room <SafeIcon name="ArrowRight" /></a>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Experience() {
  const {url}=useSiteImages();
  return (
    <section className="experience" id="experience">
      <div className="section experience__intro" style={{backgroundImage: `linear-gradient(90deg, rgba(29,48,40,.9), rgba(29,48,40,.72)), url("${url('experience_intro')}")`, backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <Reveal><Eyebrow light>A LITTLE OF EVERYTHING</Eyebrow><h2>Follow the feeling<br /><em>of the island.</em></h2></Reveal>
        <p>Adventure when you want it. A slower pace whenever you need it.</p>
      </div>
      {experiences.map((item,index)=> (
        <div className={`experience-row ${index % 2 ? 'experience-row--reverse' : ''}`} key={item.title}>
          <Reveal className="experience-row__image">
            <OptimizedImage src={url(`exp_${index + 1}`)} alt={item.title} loading="lazy" sizes="(max-width: 720px) 86vw, 45vw" width={1200} height={800} />
            <span className="experience-row__index">0{index + 1} / 03</span>
          </Reveal>
          <Reveal className="experience-row__copy" delay={0.12}>
            <span className="experience-row__icon"><SafeIcon name={item.icon} /></span>
            <Eyebrow light>{item.label}</Eyebrow>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </Reveal>
        </div>
      ))}
    </section>
  );
}

function Gallery() {
  const [filter,setFilter]=useState('All');
  const [selected,setSelected]=useState(null);
  const {items}=useGallery();
  const extra=[...new Set(items.map((item)=> item.category))].filter((name)=> name && !GALLERY_CATEGORIES.includes(name));
  const categories=['All',...GALLERY_CATEGORIES.filter((name)=> items.some((item)=> item.category===name)),...extra];
  const livePhotos=items.map((item)=> ({key: item.id,category: item.category,title: item.title,src: item.url}));
  const visiblePhotos=filter==='All' ? livePhotos : livePhotos.filter((photo)=> photo.category===filter);
  return (
    <section className="section gallery" id="gallery">
      <div className="section-heading">
        <Reveal><Eyebrow>A GLIMPSE OF YALA</Eyebrow><h2>Let the light <em>in.</em></h2></Reveal>
        <p>Little moments, open skies and the beauty of being here.</p>
      </div>
      <div className="gallery__filters" role="tablist" aria-label="Filter gallery">
        {categories.map((category)=> (
          <button className={filter===category ? 'is-active' : ''} key={category} onClick={()=> setFilter(category)} role="tab" aria-selected={filter===category}>{category}</button>
        ))}
      </div>
      <div className="gallery-grid" style={{gridAutoFlow: 'row dense'}}>
        <AnimatePresence mode="popLayout">
          {visiblePhotos.map((photo)=> (
            <motion.button className="gallery-tile" key={photo.key} layout initial={{opacity: 0,y: 15}} animate={{opacity: 1,y: 0}} exit={{opacity: 0,scale: 0.97}} transition={{duration: 0.3}} onClick={()=> setSelected(photo)} aria-label={`View ${photo.title}`}>
              <OptimizedImage src={photo.src} alt={photo.title} loading="lazy" sizes="(max-width: 720px) 46vw, 22vw" width={900} height={700} />
              <span className="gallery-tile__caption">{photo.title}<SafeIcon name="ArrowUpRight" /></span>
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {selected && (
          <motion.div className="lightbox" role="dialog" aria-modal="true" aria-label={selected.title} initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} onClick={()=> setSelected(null)}>
            <button className="lightbox__close" onClick={()=> setSelected(null)} aria-label="Close image"><SafeIcon name="X" /></button>
            <OptimizedImage src={selected.src} alt={selected.title} loading="eager" sizes="(max-width: 720px) 88vw, 86vw" width={1600} height={1100} onClick={(event)=> event.stopPropagation()} />
            <p>{selected.title}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Testimonials() {
  const [active,setActive]=useState(0);
  const change=(direction)=> setActive((current)=> (current + direction + reviews.length) % reviews.length);
  return (
    <section className="testimonials">
      <div className="testimonials__side"><Eyebrow light>WORDS FROM THE JOURNEY</Eyebrow><h2>Stories that<br /><em>stay with you.</em></h2><p>Shared by guests who found their way to Yala.</p></div>
      <div className="quote-card">
        <div className="quote-card__stars" aria-label="5 out of 5 stars">{[1,2,3,4,5].map((star)=> <SafeIcon name="Star" key={star} />)}</div>
        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{opacity: 0,y: 12}} animate={{opacity: 1,y: 0}} exit={{opacity: 0,y: -12}} transition={{duration: 0.25}}>
            <blockquote>“{reviews[active].quote}”</blockquote>
            <div className="quote-card__guest"><span>{reviews[active].initials}</span><div><strong>{reviews[active].name}</strong><small>{reviews[active].from}</small></div></div>
          </motion.div>
        </AnimatePresence>
        <div className="quote-card__controls"><span>0{active + 1} <i>/ 0{reviews.length}</i></span><div><button onClick={()=> change(-1)} aria-label="Previous review"><SafeIcon name="ArrowLeft" /></button><button onClick={()=> change(1)} aria-label="Next review"><SafeIcon name="ArrowRight" /></button></div></div>
      </div>
    </section>
  );
}

function Location() {
  const locationUrl='https://maps.app.goo.gl/xJrbjkiDN1htgPXF8';
  return (
    <section className="section location" id="location">
      <div className="location__copy">
        <Reveal><Eyebrow>THE SOUTHERN WILDS</Eyebrow><h2>A lovely place<br />to <em>begin.</em></h2><p className="lead">Tucked into Tissamaharama, with Yala National Park within reach.</p><p>Make us your base for early safari starts, birdlife, ancient places and the sun-warmed beauty of Sri Lanka’s south.</p><a className="text-link" href={locationUrl} target="_blank" rel="noreferrer">Find us on the map <SafeIcon name="ArrowUpRight" /></a></Reveal>
        <div className="location__highlights"><div><SafeIcon name="Compass" /><span><strong>Yala National Park</strong><small>A short drive away</small></span></div><div><SafeIcon name="Navigation" /><span><strong>Tissamaharama</strong><small>Southern Province, Sri Lanka</small></span></div></div>
      </div>
      <Reveal>
        <a className="map-card" href={locationUrl} target="_blank" rel="noreferrer" aria-label="Open Leopard Bliss Yala in Google Maps: Orukemmulla, Tissamaharama 82600, Sri Lanka" style={{display: 'block'}}>
          <iframe
            title="Map showing Orukemmulla, Tissamaharama 82600, Sri Lanka"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.9113112408954!2d81.36604981094979!3d6.275390993687194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae69dfef9c7b9ed%3A0x415a5e6fa2f97d08!2sLeopard%20Bliss%20Yala!5e0!3m2!1sen!2ssg!4v1793108764397!5m2!1sen!2ssg"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{position: 'absolute',inset: 0,width: '100%',height: '100%',border: 0,pointerEvents: 'none'}}
          />
          <span className="map-card__caption"><SafeIcon name="MapPin" /> Orukemmulla, Tissamaharama 82600, Sri Lanka</span>
        </a>
      </Reveal>
    </section>
  );
}

function FinalCallout() {
  const openEnquiry = () => window.dispatchEvent(new Event('open-enquiry'));
  return (
    <section className="final-cta">
      <div className="final-cta__image" />
      <div className="final-cta__content"><Reveal><Eyebrow light>YOUR TIME IN YALA</Eyebrow><h2>Your safari escape<br /><em>awaits.</em></h2><p>Come for the wild. Stay for the welcome.</p><div className="hero__actions"><a className="button button--gold" href={BOOKING_URL} target="_blank" rel="noreferrer">Book your stay <SafeIcon name="ArrowUpRight" /></a><button className="button button--glass" type="button" onClick={openEnquiry}>Get in touch</button></div></Reveal></div>
    </section>
  );
}

function HomeScroll() {
  useEffect(()=> {
    const scrollToHash=()=> {
      const id=decodeURIComponent(window.location.hash.slice(1));
      if (id) document.getElementById(id)?.scrollIntoView({behavior: 'instant'});
    };
    const frame=window.requestAnimationFrame(scrollToHash);
    window.addEventListener('hashchange',scrollToHash);
    return ()=> {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('hashchange',scrollToHash);
    };
  },[]);
  return null;
}

function App() {
  const path=window.location.pathname.replace(/\/+$/,'');
  const isAdmin=path==='/admin' || window.location.hash.startsWith('#/admin');
  const isWebmail=path==='/webmail';
  const roomSlug=!isAdmin && !isWebmail && /^\/[A-Za-z0-9-]+$/.test(path) ? path.slice(1) : null;

  useEffect(()=> {
    if (isWebmail) window.location.replace('https://webmail.hostverge.com');
  },[isWebmail]);

  if (isWebmail) return null;
  if (isAdmin) {
    return (
      <Suspense fallback={<div style={{padding: 32}}>Loading…</div>}>
        <AdminPage />
      </Suspense>
    );
  }
  if (roomSlug) return <RoomPage slug={roomSlug} />;
  return (
    <>
      <HomeScroll />
      <SiteHeader BrandMark={BrandMark} />
      <main>
        <Hero />
        <p style={{ margin: 0, padding: '10px 7%', color: '#74776e', background: '#fbfaf6', textAlign: 'right', fontSize: 10, letterSpacing: '.04em' }}>
          Updated <time dateTime="2026-10-21">21 October 2026</time>
        </p>
        <About />
        <Rooms />
        <Experience />
        <Gallery />
        <Testimonials />
        <Location />
        <FinalCallout />
      </main>
      <SiteFooter BrandMark={BrandMark} />
      <InquiryWidget />
    </>
  );
}

export default App;